import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ChevronLeft, ChevronRight, ArrowLeft, X, 
  Bed, Bath, Maximize, Car, TreePine, Home,
  MapPin, Phone, Mail
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";
import SEO from "@/components/SEO";
import NeighbourhoodMap from "@/components/NeighbourhoodMap";
import PropertyStatusRibbon from "@/components/PropertyStatusRibbon";
import { API } from "@/config/api";
import { resolveMediaUrl } from "@/utils/resolveMediaUrl";

const formatPrice = (price) => {
  return new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: "CHF",
    maximumFractionDigits: 0,
  }).format(price);
};

const PropertyDetail = () => {
  const { id } = useParams();
  const { t } = useLanguage();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await axios.get(`${API}/properties/${id}`);
        setProperty(response.data);
        // Pre-fill message
        setFormData(prev => ({
          ...prev,
          message: `I am interested in ${response.data.title}. Please send me more information.`
        }));
      } catch (error) {
        console.error("Error fetching property:", error);
        toast.error("Property not found");
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/properties/${id}/inquiry`,
        {
          ...formData,
          property_id: id,
        },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setSubmitted(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to submit inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const nextImage = () => {
    if (property?.images) {
      setCurrentImageIndex((prev) => (prev === property.images.length - 1 ? 0 : prev + 1));
    }
  };

  const prevImage = () => {
    if (property?.images) {
      setCurrentImageIndex((prev) => (prev === 0 ? property.images.length - 1 : prev - 1));
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-byron-bg">
        <div className="h-screen skeleton" />
      </main>
    );
  }

  if (!property) {
    return (
      <main className="min-h-screen bg-byron-bg flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl text-[#1A1A1A] mb-4">Property Not Found</h1>
          <Link to="/properties" className="text-sm uppercase tracking-[0.15em] underline">
            View All Properties
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="property-detail-page">
      <SEO 
        title={property.title}
        description={property.short_description || `${property.title} - ${property.bedrooms} chambres, ${property.size_sqm}m² à ${property.location}. ${formatPrice(property.price)}${property.listing_type === 'rent' ? '/mois' : ''}`}
        canonical={`/properties/${id}`}
        type="product"
        image={resolveMediaUrl(property.images?.[0]) || '/og-image.jpg'}
      />
      {/* ===================== */}
      {/* HERO SECTION */}
      {/* ===================== */}
      <section className="pt-24 pb-16 md:pb-24 bg-byron-bg" data-testid="property-hero">
        <div className="container-byron">
          {/* Back Button */}
          <Link 
            to="/properties" 
            className="inline-flex items-center gap-2 text-[#666660] hover:text-[#1A1A1A] transition-colors mb-8"
            data-testid="back-to-properties"
          >
            <ArrowLeft size={16} />
            <span className="text-xs uppercase tracking-[0.15em]">{t('propertyDetail.back')}</span>
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Left: Gallery */}
            <div className="lg:col-span-7">
              {/* Main Image */}
              <div className="relative aspect-[4/3] overflow-hidden media-frame bg-[#F0F0F0] mb-4">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentImageIndex}
                    src={resolveMediaUrl(property.images?.[currentImageIndex])}
                    alt={`${property.title} - Image ${currentImageIndex + 1}`}
                    className="w-full h-full object-cover cursor-pointer"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    onClick={() => setShowLightbox(true)}
                  />
                </AnimatePresence>

                <PropertyStatusRibbon status={property.status} size="large" />
                
                {/* Gallery Navigation */}
                {property.images?.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 flex items-center justify-center text-[#1A1A1A] hover:bg-white transition-colors"
                      aria-label="Previous image"
                      data-testid="gallery-prev"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 flex items-center justify-center text-[#1A1A1A] hover:bg-white transition-colors"
                      aria-label="Next image"
                      data-testid="gallery-next"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </>
                )}

                {/* View All Photos Button */}
                <button
                  onClick={() => setShowLightbox(true)}
                  className="absolute bottom-4 right-4 bg-white/90 hover:bg-white px-4 py-2 text-xs uppercase tracking-[0.1em] text-[#1A1A1A] transition-colors"
                >
                  View All Photos ({property.images?.length || 1})
                </button>
              </div>

              {/* Thumbnail Strip */}
              {property.images?.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {property.images.slice(0, 6).map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`flex-shrink-0 w-20 h-14 overflow-hidden media-frame transition-all ${
                        idx === currentImageIndex 
                          ? "ring-2 ring-[#1A1A1A]" 
                          : "opacity-60 hover:opacity-100"
                      }`}
                      data-testid={`gallery-thumb-${idx}`}
                    >
                      <img src={resolveMediaUrl(img)} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                  {property.images.length > 6 && (
                    <button
                      onClick={() => setShowLightbox(true)}
                      className="flex-shrink-0 w-20 h-14 bg-[#1A1A1A] flex items-center justify-center text-white text-xs"
                    >
                      +{property.images.length - 6}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Right: Key Info Card */}
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-byron-bg p-8 lg:p-10"
              >
                {/* Location */}
                <p className="label-text mb-3">
                  {property.location}
                </p>

                {/* Title */}
                <h1 className="heading-primary mb-5" data-testid="property-title">
                  {property.title}
                </h1>

                {/* Price */}
                <p className="price-display font-normal text-[#1A1A1A] mb-6" data-testid="property-price">
                  {formatPrice(property.price)}
                </p>

                {/* Key Highlights */}
                <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b border-[#E5E5E0]">
                  <div className="flex items-center gap-3">
                    <Bed size={16} className="text-[#999]" />
                    <div>
                      <p className="label-text mb-0.5">Bedrooms</p>
                      <p className="heading-tertiary">{property.bedrooms}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Maximize size={16} className="text-[#999]" />
                    <div>
                      <p className="label-text mb-0.5">Surface</p>
                      <p className="heading-tertiary">{property.size_sqm} m²</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Bath size={16} className="text-[#999]" />
                    <div>
                      <p className="label-text mb-0.5">Bathrooms</p>
                      <p className="heading-tertiary">{property.bathrooms}</p>
                    </div>
                  </div>
                  {(property.land_sqm || property.land_size) && (
                    <div className="flex items-center gap-3">
                      <TreePine size={16} className="text-[#999]" />
                      <div>
                        <p className="label-text mb-0.5">Land</p>
                        <p className="heading-tertiary">{property.land_sqm || property.land_size} m²</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* CTAs */}
                <div className="space-y-3">
                  <a
                    href="#contact"
                    className="block w-full bg-[#1A1A1A] text-white py-4 text-xs uppercase tracking-[0.15em] text-center hover:bg-[#333] transition-colors"
                  >
                    Request Details
                  </a>
                  <a
                    href="tel:+41227001234"
                    className="block w-full border border-[#1A1A1A] text-[#1A1A1A] py-4 text-xs uppercase tracking-[0.15em] text-center hover:bg-[#1A1A1A] hover:text-white transition-colors"
                  >
                    Book a Visit
                  </a>
                </div>

                {/* Reference */}
                <p className="text-xs text-[#999] mt-6 text-center">
                  Ref: BYR-{property.id?.toString().padStart(4, '0')}
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== */}
      {/* QUICK INFO GRID */}
      {/* ===================== */}
      <section className="py-10 bg-byron-bg border-t border-[#E5E5E0]">
        <div className="container-byron">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-[10px] uppercase tracking-[0.2em] text-[#999] mb-6">Property Details</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-4">
              <div className="py-2 border-b border-[#E5E5E0]">
                <p className="text-[10px] text-[#999] uppercase tracking-wider">Bedrooms</p>
                <p className="font-heading text-xl text-[#1A1A1A]">{property.bedrooms}</p>
              </div>
              <div className="py-2 border-b border-[#E5E5E0]">
                <p className="text-[10px] text-[#999] uppercase tracking-wider">Bathrooms</p>
                <p className="font-heading text-xl text-[#1A1A1A]">{property.bathrooms}</p>
              </div>
              <div className="py-2 border-b border-[#E5E5E0]">
                <p className="text-[10px] text-[#999] uppercase tracking-wider">Living Area</p>
                <p className="font-heading text-xl text-[#1A1A1A]">{property.size_sqm} m²</p>
              </div>
              <div className="py-2 border-b border-[#E5E5E0]">
                <p className="text-[10px] text-[#999] uppercase tracking-wider">Land Size</p>
                <p className="font-heading text-xl text-[#1A1A1A]">{property.land_sqm || property.land_size || '—'} m²</p>
              </div>
              <div className="py-2 border-b border-[#E5E5E0]">
                <p className="text-[10px] text-[#999] uppercase tracking-wider">Parking</p>
                <p className="font-heading text-xl text-[#1A1A1A]">{property.parking || '2 spaces'}</p>
              </div>
              <div className="py-2 border-b border-[#E5E5E0]">
                <p className="text-[10px] text-[#999] uppercase tracking-wider">Condition</p>
                <p className="font-heading text-xl text-[#1A1A1A]">{property.condition || 'Excellent'}</p>
              </div>
              <div className="py-2 border-b border-[#E5E5E0]">
                <p className="text-[10px] text-[#999] uppercase tracking-wider">Style</p>
                <p className="font-heading text-xl text-[#1A1A1A]">{property.style || 'Contemporary'}</p>
              </div>
              <div className="py-2 border-b border-[#E5E5E0]">
                <p className="text-[10px] text-[#999] uppercase tracking-wider">Reference</p>
                <p className="font-heading text-xl text-[#1A1A1A]">BYR-{property.id?.toString().padStart(4, '0')}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===================== */}
      {/* DESCRIPTION SECTION */}
      {/* ===================== */}
      <section className="py-10 md:py-14 bg-byron-bg">
        <div className="container-byron">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="label-text mb-6">About This Property</h2>
              
              {/* Short Intro */}
              <p className="heading-secondary mb-6">
                {property.short_description || `An exceptional ${property.property_type} offering refined living in one of Geneva's most sought-after locations.`}
              </p>

              {/* Full Description */}
              <div className="body-text" data-testid="property-description">
                <p>{property.description}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===================== */}
      {/* LOCATION SECTION */}
      {/* ===================== */}
      <section className="py-10 md:py-14 bg-byron-bg border-t border-[#E5E5E0]">
        <div className="container-byron">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-xs uppercase tracking-[0.2em] text-[#666660] mb-8">Location</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <div className="flex items-start gap-3 mb-6">
                  <MapPin size={20} className="text-[#C6A87C] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-heading text-lg text-[#1A1A1A]">{property.location}</p>
                    <p className="text-[#666660] text-sm">Geneva, Switzerland</p>
                  </div>
                </div>
                <p className="text-[#666660] text-sm leading-relaxed">
                  {property.location_description || `Located in the prestigious ${property.location} area, this property offers easy access to Geneva's finest amenities, international schools, and the lake.`}
                </p>
              </div>
              <div className="lg:col-span-8">
                <NeighbourhoodMap
                  location={property.location}
                  area={property.area || "Geneva"}
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===================== */}
      {/* CONTACT SECTION - Light background */}
      {/* ===================== */}
      <section id="contact" className="py-10 md:py-14 bg-white border-t border-[#E5E5E0]">
        <div className="container-byron">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Left: Contact info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-4"
            >
              <h2 className="font-heading text-2xl md:text-3xl font-light text-[#1A1A1A] mb-6 leading-tight">
                Interested in this property?
              </h2>
              <p className="text-[#666] leading-relaxed mb-8">
                Our team is available to provide detailed information, arrange a private viewing, or answer any questions you may have.
              </p>
              
              <div className="space-y-4">
                <a href="tel:+41227001234" className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#8B7355] transition-colors">
                  <Phone size={18} className="text-[#8B7355]" />
                  <span>+41 22 700 12 34</span>
                </a>
                <a href="mailto:contact@byron-properties.ch" className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#8B7355] transition-colors">
                  <Mail size={18} className="text-[#8B7355]" />
                  <span>contact@byron-properties.ch</span>
                </a>
              </div>
            </motion.div>

            {/* Right: Simple Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-7 lg:col-start-6"
            >
              {submitted ? (
                <div className="py-12">
                  <div className="w-16 h-16 border border-[#8B7355] rounded-full flex items-center justify-center mb-6">
                    <svg className="w-8 h-8 text-[#8B7355]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="font-heading text-2xl text-[#1A1A1A] mb-4">{t('propertyDetail.thankYou')}</h3>
                  <p className="text-[#666]">{t('propertyDetail.wellBeInTouch')}</p>
                </div>
              ) : (
                <div className="bg-byron-bg p-8 md:p-10">
                  <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#999] mb-8">Request Information</h3>
                  
                  <form onSubmit={handleSubmit} data-testid="inquiry-form">
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-[10px] uppercase tracking-[0.1em] text-[#999] mb-2">
                            {t('contact.firstName')}
                          </label>
                          <input
                            type="text"
                            value={formData.first_name}
                            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                            required
                            className="w-full bg-transparent border-b border-[#D4CBC2] py-3 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none"
                            data-testid="inquiry-first-name"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase tracking-[0.1em] text-[#999] mb-2">
                            {t('contact.lastName')}
                          </label>
                          <input
                            type="text"
                            value={formData.last_name}
                            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                            required
                            className="w-full bg-transparent border-b border-[#D4CBC2] py-3 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none"
                            data-testid="inquiry-last-name"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-[10px] uppercase tracking-[0.1em] text-[#999] mb-2">
                            {t('contact.email')}
                          </label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                            className="w-full bg-transparent border-b border-[#D4CBC2] py-3 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none"
                            data-testid="inquiry-email"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase tracking-[0.1em] text-[#999] mb-2">
                            {t('contact.phone')}
                          </label>
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            required
                            className="w-full bg-transparent border-b border-[#D4CBC2] py-3 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none"
                            data-testid="inquiry-phone"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-[0.1em] text-[#999] mb-2">
                          {t('contact.message')}
                        </label>
                        <textarea
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          required
                          rows={4}
                          className="w-full bg-white border border-[#D4CBC2] p-4 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none resize-none"
                          data-testid="inquiry-message"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="bg-[#1A1A1A] text-white py-4 px-8 text-xs uppercase tracking-[0.15em] hover:bg-[#333] transition-colors flex items-center gap-2"
                        data-testid="inquiry-submit"
                      >
                        {submitting ? t('contact.sending') : t('propertyDetail.sendInquiry')}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===================== */}
      {/* STICKY CTA (Mobile) */}
      {/* ===================== */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-[#E5E5E0] lg:hidden z-40">
        <a
          href="#contact"
          className="block w-full bg-[#1A1A1A] text-white py-4 text-xs uppercase tracking-[0.15em] text-center"
        >
          Request Details
        </a>
      </div>

      {/* ===================== */}
      {/* FULLSCREEN LIGHTBOX */}
      {/* ===================== */}
      <AnimatePresence>
        {showLightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-50 flex items-center justify-center"
          >
            {/* Close Button */}
            <button
              onClick={() => setShowLightbox(false)}
              className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors z-10"
              aria-label="Close gallery"
            >
              <X size={32} />
            </button>

            {/* Image */}
            <motion.img
              key={currentImageIndex}
              src={resolveMediaUrl(property.images?.[currentImageIndex])}
              alt={`${property.title} - Image ${currentImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
            />

            {/* Navigation */}
            {property.images?.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-6 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors"
                  aria-label="Previous image"
                >
                  <ChevronLeft size={48} />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-6 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors"
                  aria-label="Next image"
                >
                  <ChevronRight size={48} />
                </button>
              </>
            )}

            {/* Counter */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm">
              {currentImageIndex + 1} / {property.images?.length || 1}
            </div>

            {/* Thumbnails */}
            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2">
              {property.images?.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`w-12 h-8 overflow-hidden transition-opacity ${
                    idx === currentImageIndex ? "opacity-100 ring-1 ring-white" : "opacity-40 hover:opacity-70"
                  }`}
                >
                  <img src={resolveMediaUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===================== */}
      {/* FLOOR PLAN LIGHTBOX */}
    </main>
  );
};

export default PropertyDetail;
